import { type NumberSchema, type Reference, type StringSchema, addMethod, number, string } from 'yup';
import { dateString } from '../functions';

function lengths(this: StringSchema, length1: number | string, length2: number | string): StringSchema {
  return this.test({
    name: 'lengths',
    params: { length1, length2 },
    message: 'Il campo \'${path}\' deve essere lungo esattamente ${length1} oppure ${length2} caratteri',
    test: (value) => !value || [String(length1), String(length2)].includes(String(String(value).length)),
  });
}

function alphanum(this: StringSchema): StringSchema {
  return this.test({
    name: 'alphanum',
    message: 'Il campo \'${path}\' deve contenere solamente caratteri alfanumerici',
    test: (value) => !value || (typeof value === 'string' && RegExp(/^[a-zA-Z0-9]*$/).test(value)),
  });
}

function num(this: StringSchema): StringSchema {
  return this.test({
    name: 'num',
    message: 'Il campo \'${path}\' deve contenere solamente caratteri numerici',
    test: (value) => !value || (typeof value === 'string' && RegExp(/^\d*$/).test(value)),
  });
}

function alphabetic(this: StringSchema): StringSchema {
  return this.test({
    name: 'alphabetic',
    message: 'Il campo \'${path}\' deve contenere solamente caratteri alfabetici',
    test: (value) => !value || (typeof value === 'string' && RegExp(/^[a-zA-Z]*$/).test(value)),
  });
}

function price(this: StringSchema): StringSchema {
  return this.test({
    name: 'price',
    message: 'Il campo \'${path}\' deve essere un importo valido',
    test: (value) => !value || (typeof value === 'string' && RegExp(/^\d+((,|\.)\d{2})?$/).test(value)),
  });
}

const maxDateMessage = (ctx: { path: string; max: string }) => `Il campo '${ctx.path}' deve essere precedente o uguale alla data ${dateString(ctx.max)}`;
const minDateMessage = (ctx: { path: string; min: string }) => `Il campo '${ctx.path}' deve essere successivo o uguale alla data ${dateString(ctx.min)}`;

function minDate(this: StringSchema, min: string | Reference<string>, message = minDateMessage): StringSchema {
  return this.test({
    message,
    name: 'minDate',
    exclusive: true,
    params: { min },
    skipAbsent: true,
    test: (value, context) => !value || new Date(value).valueOf() >= new Date(context.resolve(min)).valueOf(),
  });
}

function maxDate(this: StringSchema, max: string | Reference<string>, message = maxDateMessage): StringSchema {
  return this.test({
    message,
    name: 'maxDate',
    exclusive: true,
    params: { max },
    skipAbsent: true,
    test: (value, context) => !value || new Date(value).valueOf() <= new Date(context.resolve(max)).valueOf(),
  });
}

function phone(this: StringSchema): StringSchema {
  // eslint-disable-next-line no-useless-escape
  const mobileNumberRegexp = /^([\+][0-9]{1,3}([ \.\-])?)?([\(]{1}[0-9]{3}[\)])?([0-9A-Z \.\-]{1,32})((x|ext|extension)?[0-9]{1,4}?)$/;
  return this.test({
    name: 'phone-number',
    message: 'Numero invalido',
    test: (value) => !value || mobileNumberRegexp.test(value),
  });
}

function between(this: NumberSchema, min: number, max: number): NumberSchema {
  return this.test({
    name: 'between',
    params: { min, max },
    message: 'Il campo ${path} deve essere compreso tra ${min} e ${max}',
    test: (value) => {
      if (typeof value !== 'number' || typeof value !== 'bigint') return true;
      const numberAsValue = Number(value);
      return !isNaN(numberAsValue) && numberAsValue >= min && numberAsValue <= max;
    },
  });
}

addMethod(string, 'phone', phone);
addMethod(number, 'between', between);
addMethod(string, 'minDate', minDate);
addMethod(string, 'maxDate', maxDate);
addMethod(string, 'lengths', lengths);
addMethod(string, 'alphanum', alphanum);
addMethod(string, 'num', num);
addMethod(string, 'alphabetic', alphabetic);
addMethod(string, 'price', price);
